import React, { forwardRef, type Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { type HTMLMotionProps, motion } from 'framer-motion';

import { interactiveHoverVariants } from '@/animations';

const MotionLink = motion.create(Link);

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children?: React.ReactNode;
  isIconOnly?: boolean;
}

type ButtonAsButton = HTMLMotionProps<'button'> & { as?: 'button' } & ButtonBaseProps & {
    noMotion?: boolean;
  };
type ButtonAsLink = Omit<LinkProps, 'to'> & { as: 'link'; to: string } & ButtonBaseProps & {
    noMotion?: boolean;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-xs gap-1.5 min-h-[36px]',
  md: 'px-6 py-3 text-sm gap-2 min-h-[44px]',
  lg: 'px-8 py-4 text-base gap-3 min-h-[52px]',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-soft hover:shadow-premium hover:-translate-y-0.5 hover:brightness-110 active:scale-95',
  secondary:
    'border-2 border-primary-100 bg-white/60 text-primary-600 backdrop-blur-sm hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 hover:shadow-soft hover:-translate-y-0.5 active:scale-95',
  outline:
    'border-2 border-neutral-200 bg-transparent text-neutral-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-600 active:scale-95',
  ghost:
    'border border-transparent bg-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800 active:scale-95',
};

const darkVariantClasses: Record<ButtonVariant, string> = {
  primary: 'dark:from-primary-600 dark:to-primary-700 dark:text-white',
  secondary:
    'dark:border-neutral-700 dark:bg-neutral-900/60 dark:text-primary-400 dark:hover:border-primary-600 dark:hover:bg-neutral-900 dark:hover:text-primary-300',
  outline:
    'dark:border-neutral-700 dark:text-neutral-400 dark:hover:border-primary-600 dark:hover:bg-primary-950/20 dark:hover:text-primary-400',
  ghost: 'dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-300',
};

const disabledClasses =
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:brightness-100 disabled:active:scale-100';

const ButtonInner = (props: ButtonProps, ref: Ref<HTMLButtonElement | HTMLAnchorElement>) => {
  const {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    leftIcon,
    rightIcon,
    children,
    className = '',
    as,
    noMotion,
    isIconOnly = false,
  } = props;

  const sizeCls = isIconOnly
    ? {
        sm: 'p-0 text-xs h-9 w-9 gap-0 flex-shrink-0',
        md: 'p-0 text-sm h-11 w-11 gap-0 flex-shrink-0',
        lg: 'p-0 text-base h-13 w-13 gap-0 flex-shrink-0',
      }[size]
    : sizeClasses[size];

  const variantCls = variantClasses[variant];
  const darkCls = darkVariantClasses[variant];
  const widthCls = fullWidth ? 'w-full' : '';
  const commonCls = `font-display relative inline-flex items-center justify-center overflow-hidden rounded-2xl font-bold transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${sizeCls} ${variantCls} ${darkCls} ${disabledClasses} ${widthCls} ${className}`;

  const content = isIconOnly ? (
    children || leftIcon || rightIcon
  ) : (
    <>
      {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      {children && <span className="truncate">{children}</span>}
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </>
  );

  if (noMotion) {
    if (as === 'link') {
      const {
        as: _a,
        noMotion: _nm,
        variant: _v,
        size: _s,
        fullWidth: _fw,
        leftIcon: _li,
        rightIcon: _ri,
        className: _c,
        isIconOnly: _io,
        to: _to,
        ...linkRest
      } = props;
      return (
        <Link
          to={props.to}
          type="button"
          className={commonCls}
          ref={ref as Ref<HTMLAnchorElement>}
          {...(linkRest as any)}>
          {content}
        </Link>
      );
    }
    const {
      as: _a,
      noMotion: _nm,
      variant: _v,
      size: _s,
      fullWidth: _fw,
      leftIcon: _li,
      rightIcon: _ri,
      className: _c,
      isIconOnly: _io,
      ...buttonRest
    } = props;
    return (
      <button
        type="button"
        className={commonCls}
        ref={ref as Ref<HTMLButtonElement>}
        {...(buttonRest as any)}>
        {content}
      </button>
    );
  }

  if (as === 'link') {
    const {
      as: _a,
      noMotion: _nm,
      variant: _v,
      size: _s,
      fullWidth: _fw,
      leftIcon: _li,
      rightIcon: _ri,
      className: _c,
      isIconOnly: _io,
      to: _to,
      ...linkRest
    } = props;
    return (
      <MotionLink
        to={props.to}
        className={commonCls}
        whileHover="hover"
        whileTap="tap"
        variants={interactiveHoverVariants}
        ref={ref as Ref<HTMLAnchorElement>}
        {...(linkRest as any)}>
        {content}
      </MotionLink>
    );
  }

  const {
    as: _a,
    noMotion: _nm,
    variant: _v,
    size: _s,
    fullWidth: _fw,
    leftIcon: _li,
    rightIcon: _ri,
    className: _c,
    isIconOnly: _io,
    ...buttonRest
  } = props;
  return (
    <motion.button
      type="button"
      className={commonCls}
      whileHover="hover"
      whileTap="tap"
      variants={interactiveHoverVariants}
      ref={ref as Ref<HTMLButtonElement>}
      {...buttonRest}>
      {content}
    </motion.button>
  );
};

const Button = forwardRef(ButtonInner) as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>
>;

export type { ButtonProps, ButtonSize, ButtonVariant };
export default Button;
